import style from "@styles/containers/oraculo/audiences.module.scss";

import { Button, Drawer, Input, Loading, Tabs } from "@components";
import { ForEach, If } from "@components/control-statements";
import OcdpAPI from "@providers/OcdpAPI2";
import { useEffect, useState } from "react";
import { AudienceTableItem } from "./AudienceTableItem";
import ROLES from "data/Roles";
import { UserStorage } from "shared/tools/UserStorage";

const tabs = {
  SAVED: {
    key: "SAVED",
    name: "Meus públicos",
  },
  SUGGESTED: {
    key: "SUGGESTED",
    name: "Públicos sugeridos",
  },
};

export default function AudiencesDrawer({
  visible,
  onClose,
  openAudience,
  onEditAudience,
  isSelection: is_selection,
  viewAudience,
  selectedAudience,
}) {
  const [search, setSearch] = useState("");
  const [occurrences, setOcurrences] = useState();
  const [selected_audience, setSelectedAudience] = useState(null);
  const [filteredAudiences, setFilteredAudiences] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(selectedAudience);
  const [activeTab, setActiveTab] = useState(tabs.SAVED.key);
  const [segments, setSegments] = useState([]);

  const selectedAudienceId =
    typeof selectedAudience === "number"
      ? selectedAudience
      : selectedAudience?.id;

  useEffect(() => {
    setSelected(selectedAudienceId);
  }, [selectedAudienceId]);

  const fetchData = async () => {
    setLoading(true);

    const segments = await fetch("/Segments.json").then((res) => res.json());
    setSegments(segments);

    const ocdp_api = new OcdpAPI();

    function sortAudiences(a, b) {
      if (a.id === selectedAudienceId) return -1;
      if (b.id === selectedAudienceId) return 1;
      else return 0;
    }

    let audiences = await ocdp_api.audiences();
    audiences = audiences.sort(sortAudiences);

    setAudiences(
      audiences.map((a) => ({
        id: a.id,
        name: a.name,
        type: a.type,
        status: a.status,
        brand_id: a.brand_id,
        brand: a.brand || { id: a.brand_id },
        unit_id: a.unit_id,
        unit: a.unit || { id: a.unit_id },
        schema: a.schema,
        filters: a.filters,
        boxes: a.boxes,
        links: a.links,
        version: a.version,
        execution_id: a.execution_id,
        qtd_customers: a.customers || 0,
        qtv_variables: a.schema?.length || 0,
        sort: a.extras?.sort,
        limit: a.extras?.limit,
      }))
    );
    setLoading(false);
  };
  

  useEffect(() => {
    if (visible) {
      fetchData();
    } else {
      setAudiences([]);
      setSelectedAudience(null);
      setFilteredAudiences([]);
      setOcurrences(null);
    }
  }, [visible]);

  useEffect(() => {
    if (search) {
      const lower_search = search.toLowerCase();
      const _audiences = audiences.filter(
        (a) => a.name?.toLowerCase().indexOf(lower_search) > -1
      );
      setFilteredAudiences(_audiences);
      setOcurrences(_audiences.length);
    } else {
      setFilteredAudiences(audiences);
      setOcurrences(audiences.length);
    }
  }, [search, audiences]);

  const searchAudience = (text) => {
    setSearch(text);
  };

  const suggestedAudiences =
    filteredAudiences.filter(
      (a) =>
        a.name?.trimStart().startsWith("[Olga] ") ||
        a.name?.trimStart().toLowerCase().startsWith("[bloqueado] ")
    ) || [];
  const savedAudiences =
    filteredAudiences.filter(
      (a) =>
        !a.name?.trimStart().startsWith("[Olga] ") &&
        !a.name?.trimStart().toLowerCase().startsWith("[bloqueado] ")
    ) || [];

  let userRole = UserStorage.getLoggedUser()?.role;

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      withoutTransition
      direction="full-center"
      showCloseButton={false}
    >
      <div className={style.oracleHeader}>
        <p className={style.title}>Públicos salvos</p>
        <div className="d-flex align-center">
          <Input
            search
            value={search}
            records={occurrences}
            placeholder="Buscar por..."
            className="mb-0 pt-0"
            design="search"
            prefix
            prefixWidth={20}
            onChange={(t) => searchAudience(t.target.value)}
          />
          <div className={style.close} onClick={onClose}>
            <i className="fas fa-times" />
          </div>
        </div>
      </div>

      <div className={style.audiencesContainer}>
        <If cond={loading}>
          <div className="w-100 h-100 d-flex align-center justify-center flex-1 pt-25 pb-25">
            <Loading />
          </div>
        </If>
        <If cond={!loading}>
          {suggestedAudiences.length > 0 && (
            <Tabs
              tabs={Object.values(tabs)}
              activeTab={activeTab}
              onTabSelected={(tab) => setActiveTab(tab)}
            />
          )}

          <If cond={filteredAudiences.length > 0}>
            {activeTab === tabs.SAVED.key && (
              <>
                {savedAudiences.length > 0 ? (
                  <div className={style.table}>
                    <table>
                      <tbody>
                        <tr>
                          <th>Nome do público</th>
                          <th>Qtd. Clientes</th>
                          <th>Tipo do público</th>
                          <th>Qtd. Variáveis</th>
                          <th>Status</th>
                          <th />
                        </tr>
                        <tr className={style.spacer}>
                          <td colSpan="6"></td>
                        </tr>

                        <ForEach
                          items={savedAudiences}
                          render={(audience) => (
                            <AudienceTableItem
                              segments={segments}
                              audience={audience}
                              onSelect={() => setSelectedAudience(audience)}
                              onEdit={(audience) => onEditAudience(audience)}
                              onSaved={() => fetchData()}
                              onDeleted={(audience) => {
                                setAudiences(
                                  audiences.filter((a) => a.id !== audience.id)
                                );
                              }}
                              allowEdit={onEditAudience}
                              viewAudience={viewAudience}
                              selectedAudience={selected_audience || selected}
                            />
                          )}
                        />
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={style.empty}>
                    <div className={style.icon}>
                      <i className="fal fa-users" />
                    </div>
                    <p>Não há públicos salvos.</p>
                  </div>
                )}
              </>
            )}

            {activeTab === tabs.SUGGESTED.key && (
              <>
                {suggestedAudiences.length > 0 ? (
                  <div className={style.table}>
                    <table>
                      <tbody>
                        <tr>
                          <th>Nome do público</th>
                          <th>Qtd. Clientes</th>
                          <th>Tipo do público</th>
                          <th>Qtd. Variáveis</th>
                          <th>Status</th>
                          <th />
                        </tr>
                        <tr className={style.spacer}>
                          <td colSpan="6"></td>
                        </tr>

                        <ForEach
                          items={suggestedAudiences}
                          render={(audience) => (
                            <AudienceTableItem
                              audience={audience}
                              onSelect={() => setSelectedAudience(audience)}
                              onEdit={(audience) => onEditAudience(audience)}
                              onSaved={() => fetchData()}
                              onDeleted={(audience) => {
                                setAudiences(
                                  audiences.filter((a) => a.id !== audience.id)
                                );
                              }}
                              allowEdit={userRole === ROLES.SUPPORT}
                              selectedAudience={selected_audience || selected}
                            />
                          )}
                        />
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={style.empty}>
                    <div className={style.icon}>
                      <i className="fal fa-users" />
                    </div>
                    <p>Não há públicos sugeridos.</p>
                  </div>
                )}
              </>
            )}
          </If>
          <If cond={filteredAudiences.length === 0}>
            <div className={style.empty}>
              <div className={style.icon}>
                <i className="fal fa-users" />
              </div>
              <p>Não há públicos salvos.</p>
              <span>
                Por meio desta ferramenta, será possível visualizar os públicos
                salvos.
              </span>
            </div>
          </If>
        </If>
      </div>

      {selected_audience && (
        <div className={style.openAudience}>
          <Button
            onClick={() => openAudience(selected_audience)}
            design="primary"
          >
            {is_selection ? "Selecionar" : "Abrir"} este público
          </Button>
        </div>
      )}
    </Drawer>
  );
}
